import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Card, Button, Input, Select } from '../components/ui';
import useLocationsStore, { Location } from '../stores/locationsStore';

const locationSchema = z.object({
	name: z.string().min(1, 'Location name is required'),
	businessName: z.string().min(1, 'Business name is required'),
	address: z.string().min(1, 'Address is required'),
	city: z.string().min(1, 'City is required'),
	state: z.string().min(1, 'State is required'),
	zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Invalid zip code format'),
	phone: z.string().min(1, 'Phone is required'),
	email: z.string().email('Invalid email format'),
	status: z.enum(['active', 'inactive']),
});

type LocationFormData = z.infer<typeof locationSchema>;

const US_STATES = [
	'Alabama',
	'Alaska',
	'Arizona',
	'Arkansas',
	'California',
	'Colorado',
	'Connecticut',
	'Delaware',
	'Florida',
	'Georgia',
	'Hawaii',
	'Idaho',
	'Illinois',
	'Indiana',
	'Iowa',
	'Kansas',
	'Kentucky',
	'Louisiana',
	'Maine',
	'Maryland',
	'Massachusetts',
	'Michigan',
	'Minnesota',
	'Mississippi',
	'Missouri',
	'Montana',
	'Nebraska',
	'Nevada',
	'New Hampshire',
	'New Jersey',
	'New Mexico',
	'New York',
	'North Carolina',
	'North Dakota',
	'Ohio',
	'Oklahoma',
	'Oregon',
	'Pennsylvania',
	'Rhode Island',
	'South Carolina',
	'South Dakota',
	'Tennessee',
	'Texas',
	'Utah',
	'Vermont',
	'Virginia',
	'Washington',
	'West Virginia',
	'Wisconsin',
	'Wyoming',
];

const AddLocation = () => {
	const navigate = useNavigate();
	const { addLocation } = useLocationsStore();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<LocationFormData>({
		resolver: zodResolver(locationSchema),
		defaultValues: {
			status: 'active',
			businessName: '200Below',
		},
	});

	const onSubmit = async (data: LocationFormData) => {
		const createPromise = new Promise<Location>(resolve => {
			setTimeout(() => {
				const newLocation: Location = {
					id: `location-${Date.now()}`,
					...data,
				};
				resolve(newLocation);
			}, 1000);
		});

		toast.promise(createPromise, {
			loading: 'Adding location...',
			success: 'Location added successfully!',
			error: 'Failed to add location',
		});

		try {
			const newLocation = await createPromise;
			addLocation(newLocation);
			navigate('/locations');
		} catch (error) {
			console.error('Error adding location:', error);
		}
	};

	return (
		<AppLayout showTopbar={false}>
			{/* Back Link */}
			<div
				className="flex items-center gap-2 text-blue-600 cursor-pointer hover:underline mb-4 text-sm font-medium"
				onClick={() => navigate('/locations')}
			>
				<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
					<path
						fillRule="evenodd"
						d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
					/>
				</svg>
				Back to Locations
			</div>

			<Card className="max-w-4xl">
				<h2 className="text-2xl font-bold mb-6 text-gray-900">Add New Location</h2>

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-5">
						{/* Location Name */}
						<Input
							label="Location Name"
							placeholder="e.g., Downtown Minneapolis"
							error={errors.name?.message}
							required
							disabled={isSubmitting}
							{...register('name')}
						/>

						{/* Business Name */}
						<Input
							label="Business Name"
							placeholder="e.g., 200Below"
							error={errors.businessName?.message}
							required
							disabled={isSubmitting}
							{...register('businessName')}
						/>

						{/* Address - Full Width */}
						<div className="md:col-span-2">
							<Input
								label="Address"
								placeholder="Enter street address"
								error={errors.address?.message}
								required
								disabled={isSubmitting}
								{...register('address')}
							/>
						</div>

						{/* City */}
						<Input
							label="City"
							placeholder="Enter city"
							error={errors.city?.message}
							required
							disabled={isSubmitting}
							{...register('city')}
						/>

						{/* State */}
						<Select label="State" error={errors.state?.message} required disabled={isSubmitting} {...register('state')}>
							<option value="">Select State</option>
							{US_STATES.map(state => (
								<option key={state} value={state}>
									{state}
								</option>
							))}
						</Select>

						{/* Zip Code */}
						<Input
							label="Zip Code"
							placeholder="e.g., 55401"
							error={errors.zipCode?.message}
							required
							disabled={isSubmitting}
							{...register('zipCode')}
						/>

						{/* Phone */}
						<Input
							type="tel"
							label="Phone"
							placeholder="e.g., (612) 555-0100"
							error={errors.phone?.message}
							required
							disabled={isSubmitting}
							{...register('phone')}
						/>

						{/* Email - Full Width */}
						<div className="md:col-span-2">
							<Input
								type="email"
								label="Email"
								placeholder="e.g., downtown@200below.com"
								error={errors.email?.message}
								required
								disabled={isSubmitting}
								{...register('email')}
							/>
						</div>

						{/* Status */}
						<Select label="Status" error={errors.status?.message} required disabled={isSubmitting} {...register('status')}>
							<option value="active">Active</option>
							<option value="inactive">Inactive</option>
						</Select>
					</div>

					{/* Action Buttons */}
					<div className="flex gap-3 mt-6">
						<Button type="submit" variant="primary" isLoading={isSubmitting}>
							{isSubmitting ? 'Adding Location...' : 'Add Location'}
						</Button>
						<Button type="button" variant="secondary" onClick={() => navigate('/locations')} disabled={isSubmitting}>
							Cancel
						</Button>
					</div>
				</form>
			</Card>

			<div className="mt-6 text-sm text-gray-500">© Beauty Business Portal</div>
		</AppLayout>
	);
};

export default AddLocation;
